'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { fireFloodlightPixel, FLOODLIGHT_ACTIVITIES } from '@/lib/floodlight';

interface Slide {
  src: string;
  mobileSrc: string;
  alt: string;
  tag: string;
  headline: string;
  headlineAccent: string;
  sub: string;
  ctaLabel: string;
  ctaHref: string;
  accentColor: string;
  overlay: string;
}

const slides: Slide[] = [
{
  src: "/assets/images/banner_1.jpeg",
  mobileSrc: "/assets/images/mobile_banner1.png",
  alt: 'Kasturi Chhatua, Flours & Sattu — available on Blinkit',
  tag: 'Available on Blinkit',
  headline: '6 Ingredients.',
  headlineAccent: 'Zero cooking.',
  sub: "Fuel your breakfast with Odisha's best — Chhatua, Flours & Sattu.",
  ctaLabel: 'Buy Now',
  ctaHref: '/products',
  accentColor: '#F9DC2A',
  overlay: 'linear-gradient(105deg, rgba(36,20,16,0.72) 0%, rgba(36,20,16,0.42) 50%, rgba(36,20,16,0.06) 100%)'
},
{
  src: "/assets/images/banner_2.jpeg",
  mobileSrc: "/assets/images/mobbile_banner2.png",
  alt: 'Kasturi Mix Chhatua — same love, same taste, now at your doorstep',
  tag: 'Available on Blinkit',
  headline: "Miss your maa's",
  headlineAccent: 'Chhatua?',
  sub: 'Same love. Same taste. Now at your doorstep.',
  ctaLabel: 'Buy Now',
  ctaHref: '/products',
  accentColor: '#F9DC2A',
  overlay: 'linear-gradient(105deg, rgba(36,20,16,0.72) 0%, rgba(36,20,16,0.42) 50%, rgba(36,20,16,0.06) 100%)'
}];


const TICKER_ITEMS = [
'No Preservatives',
'Clean Label Certified',
'Multi-Grain Goodness',
'Chakki Fresh Atta',
'Sugar-Free Options',
'Rich in Protein',
'Trusted by 10,000+ Families',
'Available on Blinkit & Instamart',
'Delivered in 10 Minutes',
'Read the Label. Know Your Food.'];


const DURATION = 6000;
const FADE_MS = 600;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [contentVisible, setContentVisible] = useState(true);
  const [displayIdx, setDisplayIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (idx: number) => {
    if (transitioning || idx === current) return;
    setTransitioning(true);
    setPrev(current);
    setContentVisible(false);
    setTimeout(() => {
      setCurrent(idx);
      setDisplayIdx(idx);
      setProgress(0);
      setContentVisible(true);
      setTimeout(() => {setPrev(null);setTransitioning(false);}, 800);
    }, FADE_MS);
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    timerRef.current = setInterval(() => {
      setCurrent((c) => {
        const next = (c + 1) % slides.length;
        setPrev(c);
        setTransitioning(true);
        setContentVisible(false);
        setTimeout(() => {
          setDisplayIdx(next);
          setProgress(0);
          setContentVisible(true);
          setTimeout(() => {setPrev(null);setTransitioning(false);}, 800);
        }, FADE_MS);
        return next;
      });
    }, DURATION);
    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + 100 / (DURATION / 80), 100));
    }, 80);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  const slide = slides[displayIdx];

  return (
    <section
      id="home"
      className="relative w-full overflow-hidden h-[68svh] min-h-[460px] max-h-[640px] md:h-[100svh] md:min-h-[580px] md:max-h-[960px]"
      style={{
        background: '#2B1512'
      }}>
      
      {/* ── Background slides ── */}
      {slides.map((s, idx) =>
      <div
        key={idx}
        className="absolute inset-0"
        style={{
          opacity: idx === current ? 1 : idx === prev ? 0 : 0,
          transition: 'opacity 1.4s cubic-bezier(0.4,0,0.2,1)',
          zIndex: idx === current ? 2 : idx === prev ? 1 : 0
        }}>
        
          {/* Gradient overlay — stronger on mobile to reduce visual noise */}
          <div
          className="absolute inset-0 z-10 pointer-events-none hidden md:block"
          style={{ background: s.overlay }} />
          {/* Mobile-specific heavy overlay for clean text legibility */}
          <div
          className="absolute inset-0 z-10 pointer-events-none md:hidden"
          style={{ background: 'linear-gradient(160deg, rgba(36,20,16,0.8) 0%, rgba(36,20,16,0.68) 40%, rgba(36,20,16,0.5) 70%, rgba(36,20,16,0.4) 100%)' }} />
        
          {/* Ken Burns — reduced scale on mobile to minimise busyness */}
          <div
          className="absolute inset-0"
          style={{
            transform: idx === current ? 'scale(1.06)' : 'scale(1)',
            transition: 'transform 7s cubic-bezier(0.25,0.46,0.45,0.94)'
          }}>
          
            <div className="absolute inset-0 md:hidden">
              <AppImage
              src={s.mobileSrc}
              alt={s.alt}
              fill
              priority={idx === 0}
              className="object-cover object-center"
              sizes="100vw" />
            </div>
            <div className="absolute inset-0 hidden md:block">
              <AppImage
              src={s.src}
              alt={s.alt}
              fill
              priority={idx === 0}
              className="object-cover object-center object-[70%_center]"
              sizes="100vw" />
            </div>

          </div>
        </div>
      )}

      {/* ── Grain texture ── */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
          opacity: 0.035
        }} />
      

      {/* ── Slide counter + nav dots (right side, vertically centered) ── */}
      <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col items-center gap-4">
        <span
          className="font-display italic text-white/20 select-none tabular-nums"
          style={{ fontSize: '3.5rem', lineHeight: 1, fontWeight: 300 }}>
          
          0{current + 1}
        </span>
        <div className="flex flex-col gap-2">
          {slides.map((_, idx) =>
          <button
            key={idx}
            onClick={() => {goTo(idx);startTimer();}}
            aria-label={`Go to slide ${idx + 1}`}
            className="relative overflow-hidden rounded-full"
            style={{
              width: '2px',
              height: idx === current ? '2.5rem' : '1rem',
              background: idx === current ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.22)',
              transition: 'height 0.5s cubic-bezier(0.16,1,0.3,1), background 0.4s ease'
            }}>
            
              {idx === current &&
            <span
              className="absolute top-0 left-0 w-full"
              style={{
                height: `${progress}%`,
                background: slide.accentColor,
                transition: 'height 0.08s linear'
              }} />

            }
            </button>
          )}
        </div>
      </div>

      {/* ── Hero content — clears fixed header, vertically centered in remaining space ── */}
      <div
        className="absolute inset-0 z-20 flex flex-col justify-center"
        style={{
          /* paddingTop clears the fixed header (~68px) + breathing room */
          paddingTop: 'clamp(5rem, 12vh, 8rem)',
          /* paddingBottom reserves space for the ticker strip (~40px) + breathing room */
          paddingBottom: 'clamp(4rem, 8vh, 6rem)'
        }}>
        
        <div className="w-full px-6 md:px-14 lg:px-20 max-w-screen-xl mx-auto">
          <div
            style={{
              maxWidth: '44rem',
              opacity: contentVisible ? 1 : 0,
              transform: contentVisible ? 'translateY(0)' : 'translateY(14px)',
              transition: `opacity ${FADE_MS}ms cubic-bezier(0.4,0,0.2,1), transform ${FADE_MS}ms cubic-bezier(0.4,0,0.2,1)`
            }}>
            
            {/* Tag pill */}
            <div className="flex items-center gap-3 mb-5">
              <span
                style={{
                  display: 'inline-block',
                  width: '2rem',
                  height: '1px',
                  background: slide.accentColor,
                  flexShrink: 0
                }} />
              
              <span
                className="uppercase tracking-[0.22em] text-white/60"
                style={{ fontSize: '0.62rem', fontFamily: 'var(--font-dm-sans)', fontWeight: 600 }}>
                
                {slide.tag}
              </span>
            </div>

            {/* Headline — bold, tight, immediately readable */}
            <h1
              className="font-display text-white mb-5"
              style={{
                fontSize: 'clamp(2.4rem, 5.5vw, 4.5rem)',
                lineHeight: 1.05,
                fontWeight: 200,
                letterSpacing: '0.02em'
              }}>
              
              {slide.headline}{' '}
              <span
                className="font-display italic"
                style={{ color: slide.accentColor, fontWeight: 300 }}>
                
                {slide.headlineAccent}
              </span>
            </h1>

            {/* Sub */}
            <p
              className="text-white/60 mb-8 font-light leading-relaxed"
              style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.05rem)', maxWidth: '36ch' }}>
              
              {slide.sub}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 items-center">
              <Link
                href={slide.ctaHref}
                onClick={() => fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.homeBuyNowButton)}
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:brightness-110 active:scale-95"
                style={{ background: slide.accentColor, color: '#2A1015' }}>

                {slide.ctaLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:translate-x-1">
                  <path d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </Link>
              <Link
                href="/products"
                onClick={() => fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.homeOurProductsButton)}
                className="inline-flex items-center gap-2 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] border border-white/25 text-white/75 hover:border-white/60 hover:text-white transition-all duration-300">

                Our Products
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile dot indicators ── */}
      <div className="absolute bottom-14 right-5 z-30 flex gap-2 md:hidden">
        {slides.map((_, idx) =>
        <button
          key={idx}
          onClick={() => {goTo(idx);startTimer();}}
          aria-label={`Go to slide ${idx + 1}`}
          className="rounded-full transition-all duration-500"
          style={{
            width: idx === current ? '1.5rem' : '0.4rem',
            height: '0.4rem',
            background: idx === current ? slide.accentColor : 'rgba(255,255,255,0.3)'
          }} />

        )}
      </div>

      {/* ── Ticker — pinned to the absolute bottom edge of the hero ── */}
      <div
        className="absolute bottom-0 left-0 right-0 z-30 overflow-hidden"
        style={{ background: 'var(--primary)', paddingTop: '0.6rem', paddingBottom: '0.6rem' }}>
        
        <div className="ticker-track">
          {Array.from({ length: 2 }).map((_, i) =>
          <React.Fragment key={i}>
              {TICKER_ITEMS.map((item) =>
            <span
              key={item}
              className="inline-block px-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary-foreground whitespace-nowrap">
              
                  {item}
                  <span className="opacity-40 mx-3">✦</span>
                </span>
            )}
            </React.Fragment>
          )}
        </div>
      </div>
    </section>);

}